// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartModel, OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
});

interface OwnProps {
    cart: CartModel;
    extendedStyles?: SavedCartsSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface SavedCartsSummaryCardStyles {
    dataTableRow?: DataTableRowProps;
    orderDateCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    subtotalCells?: DataTableCellProps;
}

export const savedCartsSummaryCardStyles: SavedCartsSummaryCardStyles = {};

const SavedCartsSummaryCard: FC<Props> = ({ language, cart, savedOrderDetailsLink, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(savedCartsSummaryCardStyles, extendedStyles));

    const orderDateDisplay = cart.orderDate
        ? getLocalizedDateTime({
              dateTime: cart.orderDate,
              language,
              options: {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
              },
          })
        : "";
    return (
        <DataTableRow className="saved-carts-summary-card-grid-item" {...styles.dataTableRow}>
            <DataTableCell {...styles.nameCells}>{cart.poNumber}</DataTableCell>
            <DataTableCell {...styles.orderDateCells}>
                <Link href={`${savedOrderDetailsLink?.url}?cartId=${cart.id}`}>{orderDateDisplay}</Link>
            </DataTableCell>
            <DataTableCell {...styles.shipToCells}>
                {cart.fulfillmentMethod === "PickUp" && cart.defaultWarehouse
                    ? cart.defaultWarehouse.description || cart.defaultWarehouse.name
                    : (cart as any).collectionShipToLabel ?? cart.shipToLabel}
            </DataTableCell>
            <DataTableCell {...styles.subtotalCells}>{cart.orderSubTotalDisplay}</DataTableCell>
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(SavedCartsSummaryCard);
